import React, { useState } from 'react';
import {
  Check as ClearIcon,
  Block as BounceIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, BankAccountPicker, CurrencyPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { CashStatusChip, fmtDate, getErrorMessage } from './CashShared';

const STATUSES = ['ISSUED', 'CLEARED', 'BOUNCED', 'CANCELLED'];

const emptyForm = () => ({
  account_id: '', cheque_number: '', payee: '', amount: '',
  issue_date: new Date().toISOString().slice(0, 10), offset_account_id: '',
  currency_id: '', source_reference: '', notes: '',
});

// Issued cheques are memos until cleared (DR offset / CR bank). A bounced
// cheque reverses the clearing entry; only unpresented cheques can cancel.
const ChequesTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.CASH, ACTIONS.CREATE);
  const canPost = can(RESOURCES.LEDGER.CASH, ACTIONS.APPROVE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [clearDlg, setClearDlg] = useState({ open: false, item: null, clearance_date: '' });
  const [bounceDlg, setBounceDlg] = useState({ open: false, item: null, reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const openCreate = () => {
    setValues(emptyForm());
    setShowCreate(true);
  };

  const submitCreate = async () => {
    if (!values.account_id) {
      showSnackbar('Bank account is required', 'error');
      return;
    }
    if (!String(values.cheque_number ?? '').trim()) {
      showSnackbar('Cheque number is required', 'error');
      return;
    }
    if (!String(values.payee ?? '').trim()) {
      showSnackbar('Payee is required', 'error');
      return;
    }
    if (!values.amount || Number(values.amount) <= 0) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    if (!values.offset_account_id) {
      showSnackbar('Offset account (debited on clearance) is required', 'error');
      return;
    }
    const payload = {
      account_id: Number(values.account_id),
      cheque_number: values.cheque_number.trim(),
      payee: values.payee.trim(),
      amount: Number(values.amount),
      issue_date: values.issue_date,
      offset_account_id: Number(values.offset_account_id),
    };
    if (values.currency_id) payload.currency_id = Number(values.currency_id);
    if (values.source_reference?.trim()) payload.source_reference = values.source_reference.trim();
    if (values.notes?.trim()) payload.notes = values.notes.trim();
    try {
      setSaving(true);
      await ledgerAPI.issueCheque(payload);
      showSnackbar('Cheque issued', 'success');
      setShowCreate(false);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitClear = async () => {
    const payload = {};
    if (clearDlg.clearance_date) payload.clearance_date = clearDlg.clearance_date;
    try {
      await ledgerAPI.clearCheque(clearDlg.item.cheque_id, payload);
      showSnackbar('Cheque cleared and posted', 'success');
      setClearDlg({ open: false, item: null, clearance_date: '' });
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitBounce = async () => {
    if ((bounceDlg.reason?.trim() ?? '').length < 5) {
      showSnackbar('Bounce reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.bounceCheque(bounceDlg.item.cheque_id, { reason: bounceDlg.reason.trim() });
      showSnackbar('Cheque bounced', 'success');
      setBounceDlg({ open: false, item: null, reason: '' });
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.CASH}
        title="Cheques"
        subtitle="Issue, clear, bounce or cancel cheques drawn on your bank accounts"
        addLabel="Issue Cheque"
        idKey="cheque_id"
        searchPlaceholder="Search cheque #, payee..."
        emptyMessage="No cheques found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getCheques(params)}
        onAdd={canCreate ? openCreate : null}
        filters={[{ key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) }]}
        columns={[
          { label: 'Cheque #', render: (c) => <strong>{c.cheque_number}</strong> },
          { label: 'Account', render: (c) => look.accountLabel(c.account_id) },
          { label: 'Payee', render: (c) => c.payee },
          { label: 'Issue Date', render: (c) => fmtDate(c.issue_date) },
          { label: 'Amount', render: (c) => formatAccountingAmount(c.amount), align: 'right' },
          { label: 'Status', render: (c) => <CashStatusChip status={c.status} /> },
        ]}
        rowActions={(c) => {
          const actions = [];
          if (c.status === 'ISSUED' && canPost) {
            actions.push({ icon: ClearIcon, title: 'Clear (post)', color: 'primary', onClick: () => setClearDlg({ open: true, item: c, clearance_date: '' }) });
          }
          if ((c.status === 'ISSUED' || c.status === 'CLEARED') && canPost) {
            actions.push({ icon: BounceIcon, title: 'Bounce', color: 'error', onClick: () => setBounceDlg({ open: true, item: c, reason: '' }) });
          }
          if (c.status === 'ISSUED' && canCreate) {
            actions.push({
              icon: CancelIcon, title: 'Cancel unpresented cheque', color: 'default',
              onClick: () => confirm({
                title: 'Cancel cheque',
                message: `Cancel unpresented cheque ${c.cheque_number}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.cancelCheque(c.cheque_id);
                    showSnackbar('Cheque cancelled', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <LedgerFormDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Issue Cheque"
        saving={saving}
        onSubmit={submitCreate}
        values={values}
        onChange={(key, value) => setValues((p) => ({ ...p, [key]: value }))}
        fields={[
          { key: 'account_id', label: 'Drawn On (bank account)', type: 'picker', picker: BankAccountPicker, required: true, md: 6 },
          { key: 'offset_account_id', label: 'Offset Account (debited on clearance)', type: 'picker', picker: AccountPicker, required: true, md: 6, helper: 'e.g. AP control or expense account' },
          { key: 'cheque_number', label: 'Cheque Number', required: true, md: 6 },
          { key: 'payee', label: 'Payee', required: true, md: 6 },
          { key: 'amount', label: 'Amount', type: 'number', required: true, min: 0.01, md: 6 },
          { key: 'issue_date', label: 'Issue Date', type: 'date', required: true, md: 6 },
          { key: 'currency_id', label: 'Currency (optional)', type: 'picker', picker: CurrencyPicker, md: 6 },
          { key: 'source_reference', label: 'Source Reference', md: 6 },
          { key: 'notes', label: 'Notes', type: 'textarea', md: 12 },
        ]}
      />

      <LedgerFormDialog
        open={clearDlg.open}
        onClose={() => setClearDlg({ open: false, item: null, clearance_date: '' })}
        title={`Clear cheque ${clearDlg.item?.cheque_number || ''}`}
        onSubmit={submitClear}
        values={clearDlg}
        onChange={(key, value) => setClearDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'clearance_date', label: 'Clearance Date (defaults to today)', type: 'date', md: 12 }]}
      />

      <LedgerFormDialog
        open={bounceDlg.open}
        onClose={() => setBounceDlg({ open: false, item: null, reason: '' })}
        title={`Bounce cheque ${bounceDlg.item?.cheque_number || ''}`}
        onSubmit={submitBounce}
        values={bounceDlg}
        onChange={(key, value) => setBounceDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'reason', label: 'Bounce Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default ChequesTab;
